import { Directive, inject, input, linkedSignal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

/**
 * Abstract class that provides a complete implementation of ControlValueAccessor
 * that can be extended by components to easily integrate with Angular forms.
 */
@Directive()
export abstract class BaseControlValueAccessor<T> implements ControlValueAccessor {
  /**
   * Optional NgControl injection for accessing the form control properties.
   */
  ngControl: NgControl | null;
  /**
   * The public input value of the control
   */
  $value = input<T | null>(null, { alias: 'value' });
  /**
   * If true, the control is disabled
   */
  $disabled = input(false, { alias: 'disabled' });
  /**
   * The internal value of the control. If you want
   * to consume a value from the outside, use the `value` input.
   * This is a signal that will be updated when the value changes.
   */
  $_value = linkedSignal<T | null>(() => this.$value());
  /**
   * Private state that handles the ControlValueAccessor state
   * @internal
   */
  $_disabled = linkedSignal<boolean>(() => this.$disabled());
  constructor() {
    this.ngControl = inject(NgControl, { optional: true, self: true });

    /**
     * Registers the component as a ControlValueAccessor
     * so it can be used with Angular forms.
     * This allows the component to be used with reactive forms and template-driven forms.
     * It also allows the component to be used with the ngModel directive.
     */
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }
  /**
   * Callbacks to be called when the value changes or the control is touched
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTouched: () => void = () => { };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: T) => void = () => { };

  /**
   * Writes a new value to the control
   */
  writeValue(value: T): void {
    this.$_value.set(value);
  }
  /**
   * Registers a callback function that is called when the control's value changes in the UI
   */
  registerOnChange(fn: (value: T) => void): void {
    this._onChange = fn;
  }
  /**
   * Registers a callback function that is called by the forms API when the control receives a blur event
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  /**
   * Enables or disables the control
   */
  setDisabledState(isDisabled: boolean): void {
    this.$_disabled.set(isDisabled);
  }
  /**
   * This method is called when the value changes from the UI
   * and needs to notify the parent form.
   * @param value
   */
  protected onChange(value: T): void {
    this.$_value.set(value);
    this._onChange(value);
  }
  /**
   * This method is called when the control is blurred
   * and needs to notify the parent form.
   */
  onBlur(): void {
    this.onTouched(); // it is necessary for updateOn: 'blur'
  }
  /**
   * Mark the control as touched
   */
  protected markAsTouched(): void {
    this.onTouched();
  }
  /**
   * Helper to check if the control has errors
   * @returns true if the control is invalid
   */
  protected get hasErrors(): boolean {
    return this.ngControl?.invalid ?? false;
  }
  /**
   * Helper to get the errors of the control
   * @returns the errors of the control
   * @example { required: true }
   */
  protected get errors() {
    return this.ngControl?.errors ?? null;
  }
}
